import React, { useState, useEffect } from 'react';
import categoryService from '../../services/categoryService';
import { PRICING_OPTION_OPTIONS } from '../../config/productOptions';

const DEFAULT_CATEGORIES = [
  '18+',
  'Art & Craft',
  'Artificial Flowers',
  'Automobile',
  'Balloons',
  'Cake Deco',
  'Carpets & Rugs',
  'Cleaning & Hygiene',
  'Clothing',
  'Confectionery',
  'Costumes & Wigs',
  'Disposables',
  'Drinks',
  'Food Items',
  'Fragrance/Incense',
  'Frames & Book Covers',
  'Gardening',
  'Gifting',
  'Glassware',
  'Greeting Cards & Gift Bags',
  'Hair Accessories',
  'Hardware & Tools',
  'Health & Beauty',
  'Home Decor',
  'Jewellery',
  'Kitchen Equipment',
  'Kitchen Essentials',
  'Lighting/Electrical',
  'Misc.',
  'Party Supplies',
  'Pets',
  'Phone Accessories',
  'Seasonal',
  'Stationery',
  'Storage',
  'Tablecloths',
  'Toys',
  'Travel',
  'Memory Block',
  'Hire',
];

const DEFAULT_CATEGORY_OBJECTS = DEFAULT_CATEGORIES.map((name, idx) => ({
  _id: `default-${idx}`,
  name,
  subcategories: [],
}));

const PSP_CLASSIFICATION_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'Permanent_Liquidation', label: 'Permanent Liquidation' },
  { value: 'Temporary_Liquidation', label: 'Temporary Liquidation' },
  { value: 'Bulk_Stock_Purchase', label: 'Bulk Stock Purchase' },
  { value: 'Bulk&Normal_Stock_Purchase', label: 'Bulk & Normal Stock Purchase' },
  { value: 'Normal_Purchase', label: 'Normal Purchase' },
];

const mergeCategories = (fetched = []) => {
  const merged = [];
  const byKey = new Map();
  const addCategory = (category) => {
    if (!category || !category.name) return;
    const key = category.name.trim().toLowerCase();
    const existing = byKey.get(key);
    const incomingSubcategories = Array.isArray(category.subcategories)
      ? category.subcategories.filter(Boolean)
      : [];
    if (existing) {
      if (incomingSubcategories.length) {
        const seenSubs = new Set(
          (existing.subcategories || []).map((entry) => entry.trim().toLowerCase())
        );
        incomingSubcategories.forEach((entry) => {
          const trimmed = entry.trim();
          if (!trimmed) return;
          const subKey = trimmed.toLowerCase();
          if (seenSubs.has(subKey)) return;
          seenSubs.add(subKey);
          existing.subcategories.push(trimmed);
        });
      }
      return;
    }
    const entry = {
      _id: category._id || `default-${key}`,
      name: category.name.trim(),
      subcategories: incomingSubcategories.map((sub) => sub.trim()).filter(Boolean),
    };
    byKey.set(key, entry);
    merged.push(entry);
  };
  fetched.forEach(addCategory);
  DEFAULT_CATEGORY_OBJECTS.forEach(addCategory);
  return merged;
};

const ProductForm = ({
  product,
  onSave,
  onCancel,
  store,
  canUpdateMasterPrice = true,
  canUpdateMasterCost = true,
  canUpdateStorePrice = true,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '', // Added barcode field
    category: '',
    subcategory: '',
    quickPickEnabled: false,
    pspClassification: '',
    pricing_option: '',
    price: '',
    costPrice: '',
    lowStockThreshold: '',
    stock: '',
    gstApplicable: true,
    description: '',
    storePriceOverride: '',
  });
  const [categories, setCategories] = useState(DEFAULT_CATEGORY_OBJECTS);
  const [hasLoadedCategories, setHasLoadedCategories] = useState(false);

  useEffect(() => {
    if (product) {
      const basePrice = product.basePrice != null ? product.basePrice : product.price || '';
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        barcode: product.barcode || '', // Added barcode field
        category: typeof product.category === 'object'
          ? product.category?.name || ''
          : product.category || '',
        subcategory: product.subcategory || '',
        quickPickEnabled: Boolean(product.quickPickEnabled),
        pspClassification: product.pspClassification || '',
        pricing_option: product.pricing_option || '',
        price: basePrice,
        costPrice: product.costPrice || '',
        lowStockThreshold: product.lowStockThreshold ?? '',
        stock: product.stock || '',
        gstApplicable: product.gstApplicable !== undefined ? product.gstApplicable : true,
        description: product.description || '',
        storePriceOverride:
          product.storePriceOverride?.price != null ? product.storePriceOverride.price : '',
      });
    } else {
      // Reset for new product
      setFormData({
        name: '',
        sku: '',
        barcode: '',
        category: '',
        subcategory: '',
        quickPickEnabled: false,
        pspClassification: '',
        pricing_option: '',
        price: '',
        costPrice: '',
        lowStockThreshold: '',
        stock: '',
        gstApplicable: true,
        description: '',
        storePriceOverride: '',
      });
    }
  }, [product]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoryService.fetchCategories();
        const merged = mergeCategories(Array.isArray(data) ? data : []);
        setCategories(merged);
        setHasLoadedCategories(true);
      } catch (err) {
        console.error('Failed to load categories', err);
      }
    };
    loadCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'category' && value !== prev.category ? { subcategory: '' } : {}),
    }));
  };

  useEffect(() => {
    if (!formData.category) {
      if (formData.subcategory) {
        setFormData((prev) => ({ ...prev, subcategory: '' }));
      }
      return;
    }
    const selected = categories.find(
      (cat) => cat.name.trim().toLowerCase() === formData.category.trim().toLowerCase()
    );
    if (!selected) {
      return;
    }
    if (!hasLoadedCategories) {
      return;
    }
    if (!Array.isArray(selected.subcategories) || selected.subcategories.length === 0) {
      if (formData.subcategory) {
        setFormData((prev) => ({ ...prev, subcategory: '' }));
      }
      return;
    }
    const subKey = formData.subcategory.trim().toLowerCase();
    const match = selected.subcategories.find(
      (entry) => entry && entry.trim().toLowerCase() === subKey
    );
    if (!match && formData.subcategory) {
      setFormData((prev) => ({ ...prev, subcategory: '' }));
    }
  }, [formData.category, formData.subcategory, categories, hasLoadedCategories]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const selectedCategory = categories.find(
    (category) => category.name.trim().toLowerCase() === formData.category.trim().toLowerCase()
  );
  const subcategoryOptions = selectedCategory?.subcategories?.slice().sort((a, b) => a.localeCompare(b)) || [];
  if (
    formData.subcategory &&
    !subcategoryOptions.some(
      (entry) => entry.trim().toLowerCase() === formData.subcategory.trim().toLowerCase()
    )
  ) {
    subcategoryOptions.unshift(formData.subcategory);
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Product Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm" />
        </div>
        <div>
          <label htmlFor="sku" className="block text-sm font-medium text-gray-700">SKU (optional)</label>
          <input
            type="text"
            name="sku"
            value={formData.sku}
            onChange={handleChange}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
            placeholder="Leave blank if not applicable"
          />
        </div>
        <div> {/* New div for barcode */}
          <label htmlFor="barcode" className="block text-sm font-medium text-gray-700">Barcode</label>
          <input type="text" name="barcode" value={formData.barcode} onChange={handleChange} maxLength="20" className="mt-1 w-full border-gray-300 rounded-md shadow-sm" />
        </div>
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
          <input
            type="text"
            name="category"
            list="category-options"
            value={formData.category}
            onChange={handleChange}
            className="mt-1 w-full border-gray-300 rounded-md px-3 py-2 shadow-sm"
            placeholder="Select or type category (optional)"
          />
          <datalist id="category-options">
            {categories.map((category) => (
              <option key={category._id} value={category.name} />
            ))}
          </datalist>
        </div>
        <div>
          <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">Subcategory</label>
          <select
            id="subcategory"
            name="subcategory"
            value={formData.subcategory}
            onChange={handleChange}
            disabled={!selectedCategory || subcategoryOptions.length === 0}
            className="mt-1 w-full border-gray-300 rounded-md px-3 py-2 shadow-sm disabled:bg-gray-100"
          >
            <option value="">No subcategory</option>
            {subcategoryOptions.map((subcategory) => (
              <option key={subcategory} value={subcategory}>
                {subcategory}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Subcategory list is based on the selected category.
          </p>
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            name="quickPickEnabled"
            checked={Boolean(formData.quickPickEnabled)}
            onChange={handleChange}
          />
          Enable as a checkout quick pick
        </label>
        <div>
          <label htmlFor="pspClassification" className="block text-sm font-medium text-gray-700">PSP Classification</label>
          <select
            name="pspClassification"
            value={formData.pspClassification}
            onChange={handleChange}
            className="mt-1 w-full border-gray-300 rounded-md px-3 py-2 shadow-sm"
          >
            {PSP_CLASSIFICATION_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pricing_option" className="block text-sm font-medium text-gray-700">Pricing Option</label>
          <select
            id="pricing_option"
            name="pricing_option"
            value={formData.pricing_option}
            onChange={handleChange}
            className="mt-1 w-full border-gray-300 rounded-md px-3 py-2 shadow-sm"
          >
            {PRICING_OPTION_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700">Retail Price (GST Incl.)</label>
          <input
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            required={canUpdateMasterPrice}
            disabled={!canUpdateMasterPrice}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
            step="0.01"
          />
        </div>
        <div>
          <label htmlFor="costPrice" className="block text-sm font-medium text-gray-700">Cost Price</label>
          <input
            type="number"
            name="costPrice"
            value={formData.costPrice}
            onChange={handleChange}
            required={canUpdateMasterCost}
            disabled={!canUpdateMasterCost}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
            step="0.01"
          />
        </div>
        <div>
          <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700">
            Low Stock Threshold (optional)
          </label>
          <input
            type="number"
            name="lowStockThreshold"
            value={formData.lowStockThreshold}
            onChange={handleChange}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
            min="0"
            step="1"
            placeholder={
              store?.lowStockThreshold != null
                ? `Store default: ${store.lowStockThreshold}`
                : 'Leave blank to use store default'
            }
          />
        </div>
        {!product && (
          <div>
            <label htmlFor="stock" className="block text-sm font-medium text-gray-700">Initial Stock Quantity</label>
            <input type="number" name="stock" value={formData.stock} onChange={handleChange} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm" />
          </div>
        )}
        {store && (
          <div className="md:col-span-2">
            <label htmlFor="storePriceOverride" className="block text-sm font-medium text-gray-700">
              Store Price Override ({store.name})
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="number"
                name="storePriceOverride"
                value={formData.storePriceOverride}
                onChange={handleChange}
                disabled={!canUpdateStorePrice}
                className="flex-1 border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
                step="0.01"
                placeholder="Leave blank to inherit base price"
              />
              {canUpdateStorePrice && formData.storePriceOverride !== '' && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, storePriceOverride: '' }))}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Base price: {formData.price ? `$${Number(formData.price).toFixed(2)}` : 'Not set'}.
              {' '}
              {formData.storePriceOverride
                ? `This store will sell at $${Number(formData.storePriceOverride).toFixed(2)}.`
                : 'This store currently uses the base price.'}
            </p>
            {!canUpdateStorePrice && (
              <p className="mt-1 text-xs text-amber-700">You do not have permission to change store-level price overrides.</p>
            )}
          </div>
        )}
      </div>
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
        <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className="mt-1 w-full border-gray-300 rounded-md shadow-sm"></textarea>
      </div>
      <div className="flex items-center">
        <input type="checkbox" name="gstApplicable" checked={formData.gstApplicable} onChange={handleChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded" />
        <label htmlFor="gstApplicable" className="ml-2 block text-sm text-gray-900">GST Applicable (10%)</label>
      </div>
      </div>
      <div className="flex shrink-0 justify-end space-x-3 border-t border-gray-200 pt-4 mt-4">
        <button type="button" onClick={onCancel} className="min-h-11 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300">Cancel</button>
        <button type="submit" className="min-h-11 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark">Save Product</button>
      </div>
    </form>
  );
};

export default ProductForm;
